if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      connectedCallback() {
        this.cartStateController?.abort();
        this.cartStateController = new AbortController();
        this.cartConfigurations ||= new Map();
        this.cartFiles ||= new WeakMap();
        this.cartFileSequence ||= 0;
        this.configurationRoot = this.closest('product-info') || this;
        this.configurationFieldsRoot = this.closest('.product__info-container') || this;
        const options = { signal: this.cartStateController.signal };
        this.queueCartState = () => {
          if (this.cartStateQueued) return;
          this.cartStateQueued = true;
          queueMicrotask(() => {
            this.cartStateQueued = false;
            if (this.isConnected) this.syncCartState();
          });
        };
        const changed = event => {
          if (this.configurationFieldsRoot.contains(event.target) || event.target.form === this.form) this.queueCartState();
        };
        document.addEventListener('input', changed, options);
        document.addEventListener('change', changed, options);
        document.addEventListener('btl:cart-snapshot', event => {
          if (event.detail.revision < (this.minimumCartRevision || 0)) return;
          this.cartSnapshot = event.detail;
          this.queueCartState();
        }, options);
        window.addEventListener('pageshow', this.queueCartState, options);
        this.cartSnapshot = document.querySelector('btl-luxury-header')?.cartSnapshot;
        this.queueCartState();
      }

      disconnectedCallback() {
        this.cartStateController?.abort();
      }

      getCartConfiguration(data = new FormData(this.form)) {
        let hasFiles = false;
        const value = item => {
          if (Object.prototype.toString.call(item) !== '[object File]') return String(item);
          if (!item.name && !item.size) return '';
          hasFiles = true;
          if (!this.cartFiles.has(item)) this.cartFiles.set(item, ++this.cartFileSequence);
          return ['file', this.cartFiles.get(item)];
        };
        const entries = Array.from(data.entries()).filter(([name]) =>
          ['id', 'quantity', 'selling_plan'].includes(name) || name.startsWith('properties[')
        ).map(([name, item]) => [name, value(item)]);
        const controls = Array.from(new Set([
          ...this.form.elements,
          ...this.configurationFieldsRoot.querySelectorAll('input, select, textarea, [contenteditable="true"]')
        ])).filter(field => !['submit', 'button', 'reset'].includes(field.type) && !field.disabled && !field.readOnly && (!field.form || field.form === this.form));
        const fields = controls.map(field => [field.name || field.id || field.type || 'editable',
          field.type === 'file' ? Array.from(field.files).map(value) :
          field.type === 'checkbox' || field.type === 'radio' ? [field.value, field.checked] :
          field.tagName === 'SELECT' && field.multiple ? Array.from(field.selectedOptions, option => option.value) :
          field.isContentEditable ? field.textContent : field.value]);
        const properties = Object.fromEntries(entries.filter(([name, item]) => name.startsWith('properties[') && typeof item === 'string' && item !== '')
          .map(([name, item]) => [name.slice(11, -1), item]));
        return {
          signature: JSON.stringify([entries, fields]), hasFiles,
          quantity: Math.max(1, Number(data.get('quantity')) || 1),
          identity: this.cartItemIdentity({ id: data.get('id'), properties, selling_plan: data.get('selling_plan') })
        };
      }

      cartItemIdentity(item) {
        const properties = Object.entries(item.properties || {}).filter(([, value]) => value != null && value !== '')
          .map(([name, value]) => [name, String(value)]).sort(([a], [b]) => a.localeCompare(b));
        return JSON.stringify([String(item.variant_id || item.id),
          String(item.selling_plan_allocation?.selling_plan?.id || item.selling_plan || ''), properties]);
      }

      confirmCartConfiguration(configuration, response) {
        const items = response.items?.length ? response.items : [response];
        this.cartConfigurations.set(configuration.signature, {
          identities: items.map(item => this.cartItemIdentity(item)), quantity: configuration.quantity
        });
        const header = document.querySelector('btl-luxury-header');
        this.minimumCartRevision = (header?.cartRevision || 0) + 1;
        this.cartSnapshot = null;
      }

      syncCartState() {
        if (!this.cartConfigurations || !this.form || this.submitButton.classList.contains('loading')) return;
        const configuration = this.getCartConfiguration();
        const record = this.cartConfigurations.get(configuration.signature);
        const items = this.cartSnapshot?.cart?.items;
        const contains = identity => items?.filter(item => this.cartItemIdentity(item) === identity)
          .reduce((quantity, item) => quantity + item.quantity, 0) >= configuration.quantity;
        let inCart = record ? (!items || record.identities.every(contains)) : false;
        // Only server-submitted properties can be reconciled after a fresh page load.
        // Local upload identities stay in memory and are never guessed from filenames.
        if (!record && !configuration.hasFiles && items &&
            !this.configurationFieldsRoot.querySelector('btl-toggle-addon')) {
          inCart = contains(configuration.identity);
        }
        this.goToCart = Boolean(inCart && !this.error && !this.variantIdInput.disabled &&
          this.configurationRoot.dataset.customizationReady !== 'false' && !this.submitButton.disabled);
        const text = this.goToCart ? 'Go to cart' : window.variantStrings.addToCart;
        if (this.goToCart || this.submitButtonText.textContent.trim() === 'Go to cart') {
          if (this.submitButtonText.textContent !== text) this.submitButtonText.textContent = text;
        }
      }

      async visitConfiguredCart() {
        if (this.checkingConfiguredCart) return;
        this.checkingConfiguredCart = true;
        try {
          const header = document.querySelector('btl-luxury-header');
          await header?.refreshCart(true);
          this.syncCartState();
          if (this.goToCart) window.location.assign(window.routes.cart_url);
        } finally {
          this.checkingConfiguredCart = false;
        }
      }

      async createBuyNowCheckout(formData) {
        const variantId = String(formData.get('id') || '');
        const quantity = Number(formData.get('quantity') || 1);
        if (!/^\d+$/.test(variantId) || !Number.isSafeInteger(quantity) || quantity < 1) {
          throw new Error('Please choose a valid product and quantity.');
        }
        const properties = Array.from(formData.entries()).filter(([name, value]) =>
          /^properties\[.+\]$/.test(name) && (!(value instanceof File) || value.size > 0)
        );
        const files = properties.filter(([, value]) => value instanceof File);
        let uploaded = {};
        if (files.length) {
          // Upload in a new anonymous cart; never send or replace the shopper's cart cookie.
          const upload = new FormData();
          upload.set('id', variantId);
          upload.set('quantity', String(quantity));
          const plan = formData.get('selling_plan');
          if (plan) upload.set('selling_plan', plan);
          properties.forEach(([name, value]) => upload.append(name, value));
          const response = await fetch(window.routes.cart_add_url, {
            method: 'POST', credentials: 'omit', cache: 'no-store',
            headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
            body: upload
          });
          const result = await response.json();
          if (!response.ok || result.status) {
            throw new Error(result.description || 'Your images could not be uploaded. Please try again.');
          }
          const item = result.items?.find(line => String(line.id) === variantId) || result;
          if (String(item.id) !== variantId || item.quantity !== quantity) {
            throw new Error('The selected quantity is not available. Please reduce it and try again.');
          }
          uploaded = item.properties || {};
        }
        const attributes = properties.map(([name, value]) => {
          const key = name.slice(11, -1);
          if (value instanceof File) {
            const stored = uploaded[key];
            if (typeof stored !== 'string' || !/^(https:\/\/|\/\/)/.test(stored)) {
              throw new Error('An image did not finish uploading. Please try again.');
            }
            return { key, value: stored };
          }
          return { key, value: String(value) };
        });
        const main = { merchandiseId: 'gid://shopify/ProductVariant/' + variantId, quantity, attributes };
        const sellingPlan = formData.get('selling_plan');
        if (sellingPlan) main.sellingPlanId = 'gid://shopify/SellingPlan/' + sellingPlan;
        const addonRoot = this.closest('product-info') || this.closest('section');
        const addons = Array.from(addonRoot?.querySelectorAll('btl-toggle-addon') || []).filter(el =>
          el.dataset.form === this.form.id && el.dataset.addonId &&
          el.querySelector('[data-addon-toggle]')?.checked && !el.querySelector('[data-addon-toggle]')?.disabled
        );
        const lines = [main, ...addons.map(el => ({
          merchandiseId: 'gid://shopify/ProductVariant/' + el.dataset.addonId,
          quantity, attributes: [{ key: 'For product', value: el.dataset.productTitle || '' }]
        }))];
        const input = { lines };
        const country = window.Shopify?.country;
        if (/^[A-Z]{2}$/.test(country || '')) input.buyerIdentity = { countryCode: country };
        const response = await fetch('/api/2026-10/graphql.json', {
          method: 'POST', credentials: 'omit', cache: 'no-store',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            query: 'mutation BuyNow($input: CartInput!) { cartCreate(input: $input) { cart { checkoutUrl totalQuantity lines(first: 100) { pageInfo { hasNextPage } nodes { quantity attributes { key value } merchandise { ... on ProductVariant { id } } sellingPlanAllocation { sellingPlan { id } } } } } userErrors { message } warnings { message } } }',
            variables: { input }
          })
        });
        const result = await response.json();
        const created = result.data?.cartCreate;
        const errors = created?.userErrors || [];
        if (!response.ok || result.errors?.length || errors.length || !created?.cart) {
          throw new Error(errors[0]?.message || 'Checkout could not be started. Please try again.');
        }
        const cart = created.cart;
        const actual = cart.lines?.nodes || [];
        const signature = line => JSON.stringify([
          line.merchandiseId || line.merchandise?.id,
          line.sellingPlanId || line.sellingPlanAllocation?.sellingPlan?.id || '',
          (line.attributes || []).map(({key, value}) => [key, value]).sort((a,b) => a[0].localeCompare(b[0]))
        ]);
        const totals = values => {
          const map = new Map();
          values.forEach(line => { const key = signature(line); map.set(key, (map.get(key) || 0) + line.quantity); });
          return map;
        };
        const expected = totals(lines);
        const received = totals(actual);
        if (cart.lines?.pageInfo.hasNextPage || expected.size !== received.size ||
            Array.from(expected).some(([key, count]) => received.get(key) !== count)) {
          throw new Error(created.warnings?.[0]?.message || 'Checkout could not include all selected details and quantities. Please review your selection and try again.');
        }
        const url = new URL(cart.checkoutUrl);
        const allowed = [window.location.hostname, window.Shopify?.shop].filter(Boolean);
        if (url.protocol !== 'https:' || !allowed.includes(url.hostname)) {
          throw new Error('Checkout returned an invalid address. Please try again.');
        }
        return url.href;
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        if (this.form.dataset.btlCheckout !== 'true') {
          this.syncCartState();
          if (this.goToCart) { this.visitConfiguredCart(); return; }
        }

        this.handleErrorMessage();

        const checkoutLoading = this.form.dataset.btlCheckout === 'true';
        const loadingButton = checkoutLoading
          ? this.form.querySelector('[data-btl-buy-now]:not([hidden])') || this.submitButton
          : this.submitButton;
        let loadingSpinner = loadingButton.querySelector('.loading__spinner');
        if (!loadingSpinner && loadingButton !== this.submitButton) {
          const spinner = this.submitButton.querySelector('.loading__spinner');
          if (spinner) {
            loadingSpinner = spinner.cloneNode(true);
            loadingButton.appendChild(loadingSpinner);
          }
        }
        this.form.dataset.btlLoadingAction = checkoutLoading ? 'buy' : 'add';
        this.submitButton.setAttribute('aria-disabled', true);
        loadingButton.classList.add('loading');
        loadingButton.setAttribute('aria-busy', 'true');
        loadingSpinner?.classList.remove('hidden');

        if (checkoutLoading) {
          this.error = false;
          this.createBuyNowCheckout(new FormData(this.form))
            .then(url => window.location.assign(url))
            .catch(error => {
              this.handleErrorMessage(error.message || 'Checkout could not be started. Please try again.');
            })
            .finally(() => {
              loadingButton.classList.remove('loading');
              loadingButton.removeAttribute('aria-busy');
              loadingSpinner?.classList.add('hidden');
              delete this.form.dataset.btlLoadingAction;
              this.submitButton.removeAttribute('aria-disabled');
              this.syncCartState();
            });
          return;
        }

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
                const btlConfiguration = this.getCartConfiguration(formData);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        const variantId = formData.get('id');
        const quantity = parseInt(formData.get('quantity')) || 1;
        const btlCheckout = this.form.dataset.btlCheckout === 'true';
        const addonRoot = this.closest('product-info') || this.closest('section');
        const addons = Array.from(addonRoot?.querySelectorAll('btl-toggle-addon') || []).filter(el => el.dataset.form === this.form.getAttribute('id') && el.dataset.addonId && el.querySelector('[data-addon-toggle]')?.checked && !el.querySelector('[data-addon-toggle]')?.disabled);
        if (addons.length) {
          for (const [name, value] of Array.from(formData.entries())) {
            if (name === 'id' || name === 'quantity' || name === 'selling_plan') {
              formData.delete(name); formData.append('items[0][' + name + ']', value);
            } else if (name.startsWith('properties[')) {
              formData.delete(name); formData.append('items[0][properties][' + name.slice(11, -1) + ']', value);
            }
          }
          addons.forEach((el, index) => {
            const prefix = 'items[' + (index + 1) + ']';
            formData.append(prefix + '[id]', el.dataset.addonId);
            formData.append(prefix + '[quantity]', quantity);
            formData.append(prefix + '[properties][For product]', el.dataset.productTitle);
          });
        }
        config.body = formData;
        const linesUpdateDeferred = this.createCartLinesUpdateEvent(variantId, quantity);

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (!response.status && response.items?.length) Object.assign(response, response.items.find(item => String(item.id) === String(variantId)) || response.items[0]);
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: variantId,
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description, variantId);
              if (/maximum quantity.*already in your cart/i.test(response.description || '')) {
                const currentCart = document.querySelector('btl-luxury-header')?.cartSnapshot?.cart || this.cartSnapshot?.cart;
                this.purchaseErrorCartQuantity = currentCart?.items?.filter(item => String(item.variant_id || item.id) === String(variantId)).reduce((total, item) => total + Number(item.quantity || 0), 0) ?? null;
                this.dispatchEvent(new CustomEvent('btl:cart-stock-error', { bubbles: true, detail: { variantId: String(variantId) } }));
              }
              this.dispatchCartErrorEvent(response.description || response.message, 'INVALID');
              linesUpdateDeferred?.reject(new Error(response.description || response.message));

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
             } else if (btlCheckout) {
              this.resolveCartLinesUpdate(linesUpdateDeferred);
              window.location = window.Shopify.routes.root + 'checkout';
              return;
            } else if (!this.cart) {
              this.resolveCartLinesUpdate(linesUpdateDeferred);
              window.location = window.routes.cart_url;
              return;
            }

            this.resolveCartLinesUpdate(linesUpdateDeferred);

            this.confirmCartConfiguration(btlConfiguration, response);
        const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: variantId,
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
            this.dispatchCartErrorEvent(e.message || 'Network error', 'SERVICE_UNAVAILABLE');
            linesUpdateDeferred?.reject(e);
          })
          .finally(() => {
            loadingButton.classList.remove('loading');
            loadingButton.removeAttribute('aria-busy');
            loadingSpinner?.classList.add('hidden');
            delete this.form.dataset.btlLoadingAction;
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            
            this.syncCartState();

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      formatPurchaseError(message, variantId = String(this.variantIdInput.value)) {
        if (this.form.dataset.btlLoadingAction === 'buy' || !/maximum quantity.*already in your cart/i.test(message)) return message;
        variantId = String(variantId);
        const snapshot = document.querySelector('btl-luxury-header')?.cartSnapshot?.cart || this.cartSnapshot?.cart;
        const matching = (snapshot?.items || []).filter(item => String(item.variant_id || item.id) === variantId);
        const choices = (matching[0]?.options_with_values || []).filter(option => option.value && option.value !== 'Default Title');
        const selected = Array.from((this.configurationRoot || this).querySelectorAll('variant-selects input[type="radio"]:checked, variant-selects select option:checked')).map(input => input.value);
        const label = choices.length
          ? choices.map(option => String(option.name).replace(/^sizes$/i, 'Size') + ' ' + option.value).join(' · ')
          : selected.join(' / ') || 'This option';
        const quantityField = this.form.elements.namedItem('quantity');
        const fallback = Number(quantityField?.dataset?.cartQuantity);
        const count = matching.length ? matching.reduce((total, item) => total + Number(item.quantity || 0), 0) : fallback;
        return label + ': stock limit reached.' + (Number.isSafeInteger(count) && count > 0
          ? ' Your cart already has ' + count + (count === 1 ? ' unit.' : ' units.') : ' The available quantity is already in your cart.') + ' Choose another size or color, or reduce the quantity in your cart.';
      }

      handleErrorMessage(errorMessage = false, variantId = this.variantIdInput.value) {
        this.purchaseErrorVariantId = errorMessage ? String(variantId) : null;
        this.purchaseErrorCartQuantity = null;
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = this.formatPurchaseError(errorMessage, variantId);
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
          this.queueCartState?.();
        }
      }

      createCartLinesUpdateEvent(variantId, quantity) {
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return null;

        const deferred = CartLinesUpdateEvent.createPromise();
        this.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'add',
            context: 'product',
            lines: [{ merchandiseId: variantId, quantity }],
            promise: deferred.promise,
          })
        );
        return deferred;
      }

      resolveCartLinesUpdate(deferred) {
        if (!deferred) return;
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return;

        const pendingCartDataPromise = typeof CartItems !== 'undefined'
          ? CartItems.fetchCartData()
          : fetch(`${routes.cart_url}.json`).then((response) => response.json());

        pendingCartDataPromise
          .then((cart) => {
            if (!cart?.currency) return deferred.reject(new Error('Missing currency in cart response'));
            deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
          })
          .catch((e) => deferred.reject(e));
      }

      dispatchCartErrorEvent(message, code) {
        const { CartErrorEvent } = window.StandardEvents || {};
        if (!CartErrorEvent) return;
        this.dispatchEvent(new CartErrorEvent({ error: message, code }));
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
